// Converts a hex color (#rgb or #rrggbb) to an rgba() string at the given alpha.
export const hexToRgba = (hex, alpha = 1) => {
    if (typeof hex !== 'string') return hex;
    let value = hex.replace('#', '');
    if (value.length === 3) {
        value = value.split('').map((c) => c + c).join('');
    }
    if (value.length !== 6) return hex;
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Focus/highlight color follows the user's global color (Profile settings,
// stored in localStorage under 'navclr'). Exposed as CSS custom properties —
// not JS getters — because every Design System *Style.js file is a plain
// object literal evaluated once at module import time, long before Profile
// data loads. A getter's return value gets baked into a fixed string the
// moment something does `${colors.border.focus}` inside those objects, and
// never updates again. A CSS var is resolved live by the browser on every
// paint, so calling applyFocusColor() at any point after import still takes
// effect immediately, everywhere it's referenced.
const FOCUS_VAR = '--app-focus-border-color';
const FOCUS_TINT_08_VAR = '--app-focus-border-color-tint-08';
const FOCUS_TINT_12_VAR = '--app-focus-border-color-tint-12';
const FOCUS_FALLBACK = '#0288d1';

// Call this any time 'navclr' is written to localStorage (Profile save, app
// init, etc.) so every already-rendered component picks up the new color
// without a page reload.
export const applyFocusColor = (color) => {
    const value = color || FOCUS_FALLBACK;
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(FOCUS_VAR, value);
    document.documentElement.style.setProperty(FOCUS_TINT_08_VAR, hexToRgba(value, 0.08));
    document.documentElement.style.setProperty(FOCUS_TINT_12_VAR, hexToRgba(value, 0.12));
};

// Re-applies whatever is already in localStorage (from a previous session).
// Call once at app startup, before Profile data has necessarily loaded.
export const initFocusColorFromStorage = () => {
    if (typeof localStorage === 'undefined') return;
    const saved = localStorage.getItem('navclr');
    if (saved) applyFocusColor(saved);
};

export const colors = {

    // The product's own brand colour. Every brand-tinted surface reads the user's
    // global colour first (Profile → global colour, mirrored to localStorage as
    // 'navclr'); this is what it falls back to when the profile carries none — which
    // is the case on environments where `global_color` is not configured. Same value
    // the navbar (BSNavbar) and AppBaseTile already fall back to, so the page and the
    // chrome around it agree.
    brand: {
        primary: "#32009A",
    },

    text: {
        primary: "#333",
        secondary: "#666",
        disabled: "#64748B",
        required: '#d32f2f'
    },

    border: {
        default: "#E2E8F0",
        // One step darker than `default` (same slate family) — for outlines
        // that need to read clearly on their own with no adjacent fill (e.g.
        // unchecked checkbox/radio), where `default`'s pale tone all but
        // disappears.
        strong: "#CBD5E1",
        focus: `var(${FOCUS_VAR}, ${FOCUS_FALLBACK})`,
        // Translucent tints of `focus`, for selected/hover option backgrounds.
        // Precomputed by applyFocusColor() (via hexToRgba on the real hex
        // value) since CSS var() alone can't derive an alpha variant of
        // another var() — see hexToRgba(colors.border.focus, ...) call sites
        // this replaces.
        focusTint08: `var(${FOCUS_TINT_08_VAR}, ${hexToRgba(FOCUS_FALLBACK, 0.08)})`,
        focusTint12: `var(${FOCUS_TINT_12_VAR}, ${hexToRgba(FOCUS_FALLBACK, 0.12)})`,
    },

    background: {
        surface: "#ffffff",
        page: "#fafafa",
        chip: "#E1E4F3",
        disabled: "#F3F4F6",
    },

    // Before/after comparison (change logs, audit trails): the previous value on a warm
    // peach pill, the new value on a cool blue pill — neutral, not error/success.
    diff: {
        removedText: "#7A4A14",
        removedBackground: "#FBEBD7",
        addedText: "#1E4E8C",
        addedBackground: "#E6F0FC",
    },

    status: {
        error: "#d32f2f",
        success: "#2e7d32",
        warning: "#ed6c02",
        info: "#0288d1"
    }

};

// Full `border` shorthand built from colors.border.default — the single source of
// truth for the standard 1px border used across Design System components (cards,
// papers, grids, form controls). Reuse this instead of re-templating the string.
export const borders = {
    default: `1px solid ${colors.border.default}`,
};
